// Page publique du club (D-031) : /c/<adresse>, sans compte, aux couleurs du
// club. Tout vient de club_public() : matchs, résultats, équipes et créneaux,
// contact. Aucune donnée personnelle (ni nom ni photo de joueuse).
// « Viens essayer » envoie une demande au bureau et aux coachs de la catégorie.
// Partenaires (D-033) : « Match présenté par », « Ils nous soutiennent », « Devenir
// partenaire », lien de don ; affichages et clics comptés, sans suivi des personnes.
// « L'app du club est offerte par… » : le partenaire officiel de l'app (D-084), une fois confirmé.
import {
  clubLive, clubMatchEntries, clubPublic, clubPublicGroups, clubPublicPhoto, clubPublicPhotos, publicAgenda, requestEventSignup, requestPartnership, requestTrial, requestVolunteer, trackPartners,
  type PublicClub, type PublicEvent, type PublicMatch,
} from '../lib/api';
import { publicBureau } from '../lib/recus-api';
import { entryFor, entryText, type MatchEntry } from '../domain/entry';
import { EVENT_KIND_LABEL, placesLabel, placesLeft } from '../domain/club-events';
import { socialChips } from './social';
import { $, $$, html, mount, openSheet } from './dom';
import { applyBrand } from './theme';
import { offeredLine } from './official-partner';
import { partnerOffer } from '../lib/apporteurs';
import { oppCrest } from './crest';
import { consentNote, dataHref, legalFootLink } from './legal-links';
import { followState } from '../lib/follow-api';
import type { FollowGroup, FollowState } from '../domain/follow';
import { openPublicFollow, stripHtml, type PublicFollow } from './suivre';
import { supabase } from '../lib/supabase';

const WD = ['', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'];

export async function renderPublic(root: HTMLElement, slug: string, membersHref = '/'): Promise<void> {
  let data: PublicClub | null = null;
  let agenda: PublicEvent[] = [];
  let vgroups: { id: string; name: string; emoji: string; skill: string | null }[] = [];
  let entries: MatchEntry[] = [];   // entrée aux matchs à domicile, par équipe (D-106)
  let bureau: { title: string; name: string }[] = [];   // « Sur la page du club » (D-126)
  try { [data, agenda, vgroups, entries, bureau] = await Promise.all([clubPublic(slug), publicAgenda(slug).catch(() => [] as PublicEvent[]), clubPublicGroups(slug).catch(() => []),
    clubMatchEntries({ slug }).catch(() => [] as MatchEntry[]), publicBureau(slug).catch(() => [])]); } catch { /* réseau : même message */ }
  if (!data) {
    document.title = 'Page introuvable';
    mount(root, html`
      <main class="pub-missing">
        <h1>Page introuvable</h1>
        <p>Ce club n'a pas (encore) de page publique à cette adresse.</p>
        <a class="btn btn-ghost" href="${membersHref}">Espace membres</a>
      </main>`);
    return;
  }
  const { club, teams, upcoming, results } = data;
  const partners = data.partners ?? [];
  const official = data.official_partner ?? null;
  applyBrand({ id: `public:${slug}`, name: club.name, short_name: club.short_name, logo_url: club.logo, primary_color: club.color }, false);
  document.title = club.name;

  const tz = club.timezone || 'Europe/Paris';
  const fmt = (iso: string, o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('fr-FR', { timeZone: tz, ...o }).format(new Date(iso));
  const day = (iso: string) => fmt(iso, { weekday: 'short', day: 'numeric', month: 'short' });
  const hour = (iso: string) => fmt(iso, { hour: '2-digit', minute: '2-digit' }).replace(':', 'h');
  const versus = (m: PublicMatch) => (m.is_home === false ? `${m.team} à ${m.opponent}` : `${m.team} – ${m.opponent}`);
  const won = (m: PublicMatch) => (m.forfeit ? m.forfeit === 'them' : (m.score_us ?? 0) > (m.score_them ?? 0));
  const hero = upcoming.find(m => m.is_home) ?? upcoming[0];
  const others = upcoming.filter(m => m !== hero);
  // Matchs à domicile mis en avant, déplacements à part (D-108) ; pastilles par GROUPE (D-110, révise D-108) :
  // « Seniors filles », chaque carte garde son équipe engagée (SF1, SF2).
  const home = others.filter(m => m.is_home === true);
  const away = others.filter(m => m.is_home !== true);
  const groupOf = (m: PublicMatch) => m.group_id ?? teams.find(t => t.name === m.team || t.squads?.some(q => q.name === m.team))?.id ?? '';
  // Les grands d'abord (Seniors, U15…), comme la maquette ; dans une catégorie, l'ordre de la base (par nom).
  const withId = teams.filter((t): t is typeof t & { id: string } => !!t.id);
  const matchGroups = [...new Set(withId.map(t => t.category ?? ''))].reverse().flatMap(c => withId.filter(t => (t.category ?? '') === c));
  const versusH = (m: PublicMatch) => html`<b class="fw-sqn">${m.team}</b> ${m.is_home === false ? 'à' : '–'} ${m.opponent}`;
  const categories = [...new Set(teams.map(t => t.category).filter((c): c is string => !!c))];
  const logo = club.logo ? html`<img class="pub-logo" src="${club.logo}" alt="Logo du ${club.name}">`
    : html`<span class="pub-logo pub-mono" aria-hidden="true">${(club.short_name || club.name).slice(0, 3).toUpperCase()}</span>`;

  mount(root, html`
    <div class="pub">
      <header class="pub-head">
        <div class="pub-id">
          ${logo}
          <div><h1>${club.name}</h1>${club.tagline ? html`<p>${club.tagline}</p>` : ''}</div>
        </div>
        <nav class="pub-nav" aria-label="Sections">
          ${upcoming.length || results.length ? html`<button type="button" data-go="pub-matchs">Matchs</button>` : ''}
          ${teams.length ? html`<button type="button" data-go="pub-equipes">Équipes</button>` : ''}
          ${agenda.length ? html`<button type="button" data-go="pub-agenda">Agenda</button>` : ''}
          ${club.email || club.address || bureau.length ? html`<button type="button" data-go="pub-contact">Contact</button>` : ''}
          <button type="button" data-go="pub-partenaires">Partenaires</button>
          ${club.trial ? html`<button type="button" class="pub-nav-cta" data-trial>Nous rejoindre</button>` : ''}
        </nav>
      </header>

      <main class="pub-main">
        <div id="pub-live"></div>
        ${hero ? html`
          <section class="pub-hero" aria-label="Prochain match">
            <p class="pub-kicker">${hero.is_home ? 'Prochain match à domicile' : 'Prochain match'}</p>
            <div class="pub-hero-vs">${oppCrest(hero, 'opp-crest-lg', hero.opponent_logo)}<h2>${versus(hero)}</h2></div>
            <p class="pub-when">${day(hero.starts_at)} · ${hour(hero.starts_at)}${hero.location ? ` · ${hero.location}` : ''}</p>
            ${hero.competition_name ? html`<p class="pub-sub">${hero.competition_name}</p>` : ''}
            ${(() => { const t = hero.is_home ? entryText(entryFor(entries, hero)) : null; return t ? html`<p class="pub-sub">${t}</p>` : ''; })()}
            ${hero.presented_by ? html`
              <p class="pub-presented">Match présenté par
                ${hero.presented_by.website ? html`<a href="${hero.presented_by.website}" target="_blank" rel="noopener" data-pclick="${hero.presented_by.id}" data-place="match">${hero.presented_by.name}</a>`
                  : html`<strong>${hero.presented_by.name}</strong>`}</p>` : ''}
          </section>` : ''}

        ${club.trial ? html`
          <section class="pub-join">
            <p class="pub-kicker">Nous rejoindre</p>
            <h2>Venez essayer</h2>
            <p>Une séance d'essai, de l'école de basket aux seniors. Laissez vos coordonnées : le club vous propose un créneau.</p>
            <button type="button" class="btn btn-primary" data-trial>Demander une séance d'essai</button>
          </section>` : ''}

        <section class="pub-join pub-vol">
          <p class="pub-kicker">Donner un coup de main</p>
          <h2>Devenir bénévole</h2>
          <p>Buvette, table de marque, cuisine des tournois, transport… Une heure de temps en temps, et le club tourne.</p>
          ${vgroups.length ? html`<p class="pub-vgroups">${vgroups.map(g => html`<span class="chip chip-mute">${g.emoji} ${g.name}</span>`)}</p>` : ''}
          <button type="button" class="btn btn-ghost" data-vjoin>Je veux aider</button>
        </section>

        ${others.length || results.length ? html`
          <section class="pub-sec" id="pub-matchs">
            <h2>Matchs</h2>
            ${matchGroups.length > 1 ? html`<nav class="pub-filter" aria-label="Filtrer par groupe">
              <button type="button" data-group-filter="" aria-pressed="true">Tous</button>
              ${matchGroups.map(t => html`<button type="button" data-group-filter="${t.id}" aria-pressed="false">${t.name}</button>`)}
            </nav>` : ''}
            <div class="fw-strip" id="fw-strip" hidden></div>
            ${home.length ? html`
              <h3 class="pub-h3">À domicile · venez nous encourager</h3>
              <div class="pub-homes">${home.map(m => html`
                <article class="pub-home" data-group="${groupOf(m)}">
                  <span class="pub-hdate"><b>${new Date(m.starts_at).getDate()}</b><small>${new Date(m.starts_at).toLocaleDateString('fr-FR', { month: 'short' })}</small></span>
                  <span class="pub-what"><strong class="crest-line">${oppCrest(m, 'opp-crest-xs', m.opponent_logo)}<span>${versusH(m)}</span></strong>
                    <small>${day(m.starts_at)} · ${hour(m.starts_at)}${m.location ? ` · ${m.location}` : ''}</small>
                    ${(() => { const e = entryText(entryFor(entries, m)); return e ? html`<small class="pub-entry">${e}</small>` : ''; })()}</span>
                </article>`)}</div>` : ''}
            ${away.length ? html`
              <h3 class="pub-h3">En déplacement</h3>
              <ul class="pub-list pub-away">${away.map(m => html`
                <li data-group="${groupOf(m)}">
                  <span class="pub-date">${day(m.starts_at)}<br>${hour(m.starts_at)}</span>
                  <span class="pub-what"><strong class="crest-line">${oppCrest(m, 'opp-crest-xs', m.opponent_logo)}<span>${versusH(m)}</span></strong>${m.location ? html`<small>${m.location}</small>` : ''}</span>
                  <span class="chip chip-mute">${m.is_home === false ? 'Ext.' : 'Neutre'}</span>
                </li>`)}</ul>` : ''}
            <p class="muted pub-none" hidden>Pas de match à venir pour ce groupe.</p>
            ${results.length ? html`
              <h3 class="pub-h3">Derniers résultats</h3>
              <div class="pub-results">${results.map(m => html`
                <div class="pub-res ${won(m) ? 'win' : 'loss'}" data-group="${groupOf(m)}">
                  <small>${m.team}</small>
                  <b>${m.forfeit ? (m.forfeit === 'them' ? 'Victoire' : 'Défaite') : `${m.score_us}–${m.score_them}`}</b>
                  <small class="crest-line">${oppCrest(m, 'opp-crest-xs', m.opponent_logo)}<span>${m.is_home === false ? 'à' : 'c.'} ${m.opponent}</span></small>
                </div>`)}</div>` : ''}
          </section>` : ''}

        <div id="pub-photos"></div>

        ${teams.length ? html`
          <section class="pub-sec" id="pub-equipes">
            <h2>Équipes et entraînements</h2>
            <div class="pub-teams">${teams.map(t => html`
              <div class="pub-team">
                <strong>${t.name}</strong>
                ${t.slots.length ? html`<small>${t.slots.map(s => `${s.weekdays.map(d => WD[d] ?? '').join(' · ')} ${s.time.replace(':', 'h')}${s.location ? ` · ${s.location}` : ''}`).join(' / ')}</small>`
                  : html`<small>Créneaux sur demande</small>`}
              </div>`)}</div>
          </section>` : ''}

        ${data.news?.length ? html`
          <section class="pub-sec" id="pub-news">
            <h2>Actualités</h2>
            <div class="pub-news">${data.news.map(n => html`
              <article class="pub-post">
                <small class="pub-kind">${fmt(n.created_at, { day: 'numeric', month: 'long' })}${n.team ? ` · ${n.team}` : ''}</small>
                <strong>${n.title}</strong>
                ${n.body ? html`<p class="pub-desc">${n.body}</p>` : ''}
                <small class="muted">${n.author_label}</small>
              </article>`)}</div>
          </section>` : ''}

        ${agenda.length ? html`
          <section class="pub-sec" id="pub-agenda">
            <h2>Agenda du club</h2>
            <div class="pub-events">${agenda.map(ev => {
              const left = placesLeft(ev.capacity, ev.taken);
              return html`
              <article class="pub-event">
                <span class="ce-tile" aria-hidden="true"><b>${fmt(ev.starts_at, { day: 'numeric' })}</b><small>${fmt(ev.starts_at, { month: 'short' }).replace('.', '')}</small></span>
                <div class="pub-what">
                  <small class="pub-kind">${EVENT_KIND_LABEL[ev.kind]}</small>
                  <strong>${ev.title}</strong>
                  <small>${day(ev.starts_at)} · ${hour(ev.starts_at)}${ev.ends_at ? ` – ${hour(ev.ends_at)}` : ''}${ev.location ? ` · ${ev.location}` : ''}</small>
                  ${ev.partners.length ? html`<small>Avec ${ev.partners.map((pa, i) => html`${i ? ', ' : ''}${pa.website ? html`<a href="${pa.website}" target="_blank" rel="noopener" data-pclick="${pa.id}" data-place="event">${pa.name}</a>` : pa.name}`)}</small>` : ''}
                  ${ev.description ? html`<p class="pub-desc">${ev.description}</p>` : ''}
                  ${ev.signup ? html`<span class="pub-ev-signup">${left === 0 ? html`<span class="chip chip-mute">Complet</span>`
                    : html`<button type="button" class="btn btn-primary btn-sm" data-evsign="${ev.id}">S'inscrire</button>`}<small>${placesLabel(ev.capacity, ev.taken)}</small></span>` : ''}
                </div>
              </article>`;
            })}</div>
          </section>` : ''}

        <section class="pub-sec" id="pub-partenaires">
          <h2>Ils nous soutiennent</h2>
          ${official ? offeredLine(official) : ''}
          <div class="pub-partners">
            ${partners.map(pa => html`
              ${pa.website ? html`<a class="pub-partner" href="${pa.website}" target="_blank" rel="noopener" data-pclick="${pa.id}" data-place="public" title="${pa.name}">`
                : html`<span class="pub-partner" title="${pa.name}">`}
                ${pa.logo ? html`<img src="${pa.logo}" alt="${pa.name}">` : html`<b>${pa.name}</b>`}
              ${pa.website ? html`</a>` : html`</span>`}`)}
            <button type="button" class="pub-partner pub-partner-cta" data-partner>Devenir partenaire</button>
          </div>
          <a class="btn-link-accent" id="pub-formules" hidden href="${membersHref === '/app' ? '/partenaires' : `/c/${slug}/partenaires`}">Nos formules de partenariat ›</a>
          ${club.donation_url ? html`<a class="btn btn-ghost pub-donate" href="${club.donation_url}" target="_blank" rel="noopener">Faire un don au club</a>` : ''}
        </section>

        ${club.email || club.address || bureau.length ? html`
          <section class="pub-sec" id="pub-contact">
            <h2>Contact</h2>
            <div class="pub-contact">
              ${club.address ? html`<p><span class="muted">Salle</span> ${club.address}</p>` : ''}
              ${club.email ? html`<p><span class="muted">Écrire au club</span> <a href="mailto:${club.email}">${club.email}</a></p>` : ''}
              ${bureau.length ? html`<p><span class="muted">Le bureau</span> ${bureau.map(b => `${b.title} · ${b.name}`).join(' ; ')}</p>` : ''}
            </div>
          </section>` : ''}
      </main>

      <footer class="pub-foot">
        ${logo}
        <div><strong>${club.name}</strong>${club.address ? html`<span>${club.address}</span>` : ''}
          ${club.social?.length ? html`<span class="foot-social">Suivez le club ${socialChips(club.social, 'social-on-dark')}</span>` : ''}
          ${teams.filter(t => t.social?.length).map(t => html`<span class="foot-social">${t.name} ${socialChips(t.social, 'social-on-dark social-sm')}</span>`)}
          <small>Site du club · propulsé par Arceau</small>${legalFootLink(dataHref({ slug }))}</div>
        <a class="pub-members" href="${membersHref}">Espace membres</a>
      </footer>
    </div>`);

  // Score en direct (D-046) : rafraîchi toutes les 20 secondes tant que la page est ouverte.
  const live = async () => {
    const el = root.querySelector<HTMLElement>('#pub-live');
    if (!el) return;
    const list = await clubLive(slug).catch(() => []);
    mount(el, html`${list.map(l => html`
      <section class="pub-live" aria-live="polite">
        <p class="pub-kicker">● En direct · ${l.quarter <= 4 ? `Q${l.quarter}` : 'Prolongation'}${l.location ? ` · ${l.location}` : ''}</p>
        <div class="ls-score"><span class="ls-team">${l.team}</span><b>${l.us}</b><span class="ls-sep">–</span><b>${l.them}</b><span class="ls-team">${l.opponent}</span></div>
      </section>`)}`);
    if (root.isConnected && root.querySelector('#pub-live')) setTimeout(() => void live(), 20000);
  };
  void live();

  // Victoires en images (D-050) : les selfies publiés par le staff, droit à l'image confirmé.
  void (async () => {
    const el = root.querySelector<HTMLElement>('#pub-photos');
    const photos = el ? await clubPublicPhotos(slug).catch(() => []) : [];
    if (!el || !photos.length) return;
    mount(el, html`
      <section class="pub-sec" id="pub-victoires">
        <h2>Victoires en images</h2>
        <div class="pub-photos">${photos.map(p => html`
          <figure class="pub-ph" data-pid="${p.id}">
            <div class="pub-ph-img" aria-hidden="true"></div>
            <figcaption><strong>${p.caption ?? p.match}</strong>${p.caption ? html`<small>${p.match}</small>` : ''}</figcaption>
          </figure>`)}</div>
      </section>`);
    for (const p of photos) {
      const data = await clubPublicPhoto(slug, p.id).catch(() => null);
      const box = el.querySelector<HTMLElement>(`[data-pid="${p.id}"] .pub-ph-img`);
      if (!data || !box) continue;
      const img = document.createElement('img');
      img.src = `data:image/jpeg;base64,${data}`; img.alt = p.caption ?? p.match;
      box.replaceWith(img);
    }
  })();
  // Filtre par groupe (D-110) : le même groupe dans les matchs à domicile, les déplacements et les résultats ;
  // sous les pastilles, la bande du groupe : « ☆ Suivre » (un petit compte supporter si personne n'est connecté).
  let follow: FollowState | null = null, current = '';
  const groupsF = (): FollowGroup[] => matchGroups.map(t => follow?.groups.find(g => g.team_id === t.id)
    ?? { team_id: t.id, name: t.name, category: t.category, gender: t.gender, squads: t.squads ?? [], follow: null, auto: null, followers: 0 });
  const loadFollow = async () => { follow = (await supabase?.auth.getSession())?.data.session ? await followState({ slug }).catch(() => null) : null; };
  const drawStrip = () => {
    const el = $(root, '#fw-strip');
    if (!el) return;
    const g = groupsF().find(x => x.team_id === current);
    el.hidden = !g;
    if (!g) return;
    mount(el, stripHtml(g));
    $(el, '[data-follow]')!.addEventListener('click', () => openPublicFollow(pf, g));
  };
  const pf: PublicFollow = { slug, tz, upcoming, refresh: async () => { await loadFollow(); drawStrip(); } };
  void loadFollow().then(drawStrip);
  for (const fb of $$<HTMLButtonElement>(root, '[data-group-filter]')) fb.addEventListener('click', () => {
    const want = fb.dataset.groupFilter ?? '';
    current = want;
    fb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    $$(root, '[data-group-filter]').forEach(x => x.setAttribute('aria-pressed', String(x === fb)));
    const sec = $(root, '#pub-matchs')!;
    $$<HTMLElement>(sec, '[data-group]').forEach(el => { el.hidden = !!want && el.dataset.group !== want; });
    for (const list of ['.pub-homes', '.pub-away', '.pub-results']) {
      const box = $(sec, list); if (!box) continue;
      const any = $$<HTMLElement>(box, '[data-group]').some(el => !el.hidden);
      box.hidden = !any;
      const head = box.previousElementSibling as HTMLElement | null; if (head?.classList.contains('pub-h3')) head.hidden = !any;
    }
    const none = $(sec, '.pub-none') as HTMLElement | null;
    if (none) none.hidden = $$<HTMLElement>(sec, '.pub-homes [data-group], .pub-away [data-group]').some(el => !el.hidden);
    drawStrip();
  });
  for (const b of $$<HTMLButtonElement>(root, '[data-go]')) {
    b.addEventListener('click', () => document.getElementById(b.dataset.go!)?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }
  for (const b of $$<HTMLButtonElement>(root, '[data-trial]')) b.addEventListener('click', () => openTrial(slug, club.name, categories));
  for (const b of $$<HTMLButtonElement>(root, '[data-vjoin]')) b.addEventListener('click', () => openVolunteerJoin(slug, club.name, vgroups));
  for (const b of $$<HTMLButtonElement>(root, '[data-partner]')) b.addEventListener('click', () => openPartnership(slug, club.name));
  // « Nos formules de partenariat » : seulement si le club en a (D-103) ; sinon « Devenir partenaire » suffit.
  void partnerOffer(slug, null).then(o => { const a = $(root, '#pub-formules'); if (a && o && (o.packs.length || o.app)) a.hidden = false; }).catch(() => undefined);
  for (const b of $$<HTMLButtonElement>(root, '[data-evsign]')) {
    b.addEventListener('click', () => openEventSignup(slug, agenda.find(ev => ev.id === b.dataset.evsign)!, () => void renderPublic(root, slug, membersHref)));
  }
  trackPartners([...new Set(agenda.flatMap(ev => ev.partners.map(pa => pa.id)))], 'event');
  // Visibilité des partenaires : un affichage par page vue, un clic par lien suivi.
  trackPartners([...new Set([...partners.map(pa => pa.id), ...(official ? [official.id] : [])])], 'public');
  if (hero?.presented_by) trackPartners([hero.presented_by.id], 'match');
  for (const a of $$<HTMLAnchorElement>(root, '[data-pclick]')) {
    a.addEventListener('click', () => trackPartners([a.dataset.pclick!], a.dataset.place as 'public' | 'match' | 'event', true));
  }
}

/** Inscription sans compte à un événement public (tournoi, loto…). */
export function openEventSignup(slug: string, ev: PublicEvent, onDone: () => void) {
  const left = placesLeft(ev.capacity, ev.taken);
  const max = Math.min(20, left ?? 20);
  openSheet(`S'inscrire · ${ev.title}`, html`
    <form class="form" id="f-evsign" novalidate>
      <label class="field"><span>Nom (personne ou famille)</span><input name="name" maxlength="80" autocomplete="name" required></label>
      <label class="field"><span>Nombre de personnes</span><input name="people" type="number" inputmode="numeric" min="1" max="${max}" value="1"></label>
      <div class="row-2">
        <label class="field"><span>E-mail</span><input name="email" type="email" inputmode="email" maxlength="120" autocomplete="email"></label>
        <label class="field"><span>Téléphone</span><input name="phone" type="tel" inputmode="tel" maxlength="20" autocomplete="tel"></label>
      </div>
      <label class="pub-hp" aria-hidden="true">Ne pas remplir <input name="website" tabindex="-1" autocomplete="off"></label>
      <label class="check"><input type="checkbox" name="consent"><span>J'accepte que le club me contacte au sujet de cet événement. Ces coordonnées ne servent qu'à ça.</span></label>
      ${consentNote(dataHref({ slug }))}
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">M'inscrire</button>
    </form>`, (sheet) => {
    const f = $<HTMLFormElement>(sheet, '#f-evsign')!;
    const msg = (t: string) => { $(f, '.form-msg')!.textContent = t; };
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      msg('');
      const d = new FormData(f);
      const v = (k: string) => String(d.get(k) ?? '').trim();
      const done = () => mount(sheet.querySelector('.sheet-body')!, html`
        <div class="pub-thanks"><p class="pub-kicker">Inscription enregistrée</p><h3>À bientôt !</h3>
        <p>${ev.title} : le club vous attend.</p></div>`);
      if (v('website')) { done(); return; }
      const people = Number(v('people'));
      if (!v('name')) { msg('Indiquez un nom.'); return; }
      if (!Number.isInteger(people) || people < 1 || people > max) { msg(`Entre 1 et ${max} personnes.`); return; }
      if (!v('email') && !v('phone')) { msg('Laissez un e-mail ou un téléphone.'); return; }
      if (v('email') && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v('email'))) { msg('Adresse e-mail invalide.'); return; }
      if (v('phone') && !/^[0-9 +().-]{6,20}$/.test(v('phone'))) { msg('Numéro de téléphone invalide.'); return; }
      if (d.get('consent') !== 'on') { msg('Cochez la case pour que le club puisse vous contacter.'); return; }
      const btn = $<HTMLButtonElement>(f, 'button[type=submit]')!;
      btn.disabled = true;
      try {
        await requestEventSignup(slug, ev.id, { name: v('name'), email: v('email') || null, phone: v('phone') || null, people });
        done(); onDone();
      } catch (err) {
        btn.disabled = false;
        const m = String((err as Error).message);
        msg(/complet/.test(m) ? 'Plus assez de places.' : (err as { code?: string }).code === '54000' ? 'Trop de demandes : réessayez plus tard.'
          : 'Inscription impossible. Réessayez.');
      }
    });
  });
}

export function openPartnership(slug: string, clubName: string) {
  openSheet(`Devenir partenaire · ${clubName}`, html`
    <form class="form" id="f-partner" novalidate>
      <p class="muted">Visibilité sur la page du club et dans l'app des familles, « match présenté par », offre aux membres… Le club vous recontacte pour en parler.</p>
      <label class="field"><span>Entreprise ou organisme</span><input name="company" maxlength="80" autocomplete="organization" required></label>
      <label class="field"><span>Votre nom</span><input name="contact_name" maxlength="80" autocomplete="name"></label>
      <div class="row-2">
        <label class="field"><span>E-mail</span><input name="email" type="email" inputmode="email" maxlength="120" autocomplete="email"></label>
        <label class="field"><span>Téléphone</span><input name="phone" type="tel" inputmode="tel" maxlength="20" autocomplete="tel"></label>
      </div>
      <label class="field"><span>Votre projet (facultatif)</span><textarea name="message" rows="3" maxlength="500"></textarea></label>
      <label class="pub-hp" aria-hidden="true">Ne pas remplir <input name="website" tabindex="-1" autocomplete="off"></label>
      <label class="check"><input type="checkbox" name="consent"><span>J'accepte que le club me recontacte au sujet de ce partenariat. Ces coordonnées ne servent qu'à ça.</span></label>
      ${consentNote(dataHref({ slug }))}
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Envoyer</button>
    </form>`, (sheet) => {
    const f = $<HTMLFormElement>(sheet, '#f-partner')!;
    const msg = (t: string) => { $(f, '.form-msg')!.textContent = t; };
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      msg('');
      const d = new FormData(f);
      const v = (k: string) => String(d.get(k) ?? '').trim();
      const done = () => mount(sheet.querySelector('.sheet-body')!, html`
        <div class="pub-thanks"><p class="pub-kicker">Demande envoyée</p><h3>Merci !</h3>
        <p>Le bureau du club a bien reçu votre proposition et vous recontacte.</p></div>`);
      if (v('website')) { done(); return; }   // robot : on ne dit rien
      if (!v('company')) { msg('Indiquez le nom de l’entreprise ou de l’organisme.'); return; }
      if (!v('email') && !v('phone')) { msg('Laissez un e-mail ou un téléphone pour être recontacté.'); return; }
      if (v('email') && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v('email'))) { msg('Adresse e-mail invalide.'); return; }
      if (v('phone') && !/^[0-9 +().-]{6,20}$/.test(v('phone'))) { msg('Numéro de téléphone invalide.'); return; }
      if (d.get('consent') !== 'on') { msg('Cochez la case pour que le club puisse vous recontacter.'); return; }
      const btn = $<HTMLButtonElement>(f, 'button[type=submit]')!;
      btn.disabled = true;
      try {
        await requestPartnership(slug, { company: v('company'), contact_name: v('contact_name') || null, email: v('email') || null,
          phone: v('phone') || null, message: v('message') || null, consent: true });
        done();
      } catch (err) {
        btn.disabled = false;
        msg((err as { code?: string }).code === '54000' ? 'Trop de demandes pour le moment : réessayez demain.' : 'Envoi impossible. Vérifiez votre connexion et réessayez.');
      }
    });
  });
}

/** « Devenir bénévole » (D-070) : le chef du groupe choisi et le bureau sont prévenus. */
function openVolunteerJoin(slug: string, clubName: string, groups: { id: string; name: string; emoji: string; skill: string | null }[]) {
  openSheet(`Devenir bénévole · ${clubName}`, html`
    <form class="form" id="f-vjoin" novalidate>
      <label class="field"><span>Prénom et nom</span><input name="name" maxlength="80" autocomplete="name" required></label>
      ${groups.length ? html`<label class="field"><span>Envie d'aider plutôt…</span><select name="group"><option value="">Là où il faut</option>
        ${groups.map(g => html`<option value="${g.id}">${g.emoji} ${g.name}${g.skill ? ` (${g.skill})` : ''}</option>`)}</select></label>` : ''}
      <div class="row-2">
        <label class="field"><span>E-mail</span><input name="email" type="email" inputmode="email" maxlength="120" autocomplete="email"></label>
        <label class="field"><span>Téléphone</span><input name="phone" type="tel" inputmode="tel" maxlength="20" autocomplete="tel"></label>
      </div>
      <label class="field"><span>Un mot (facultatif)</span><textarea name="message" rows="3" maxlength="500" placeholder="ex. disponible le samedi, je fais de super crêpes"></textarea></label>
      <label class="pub-hp" aria-hidden="true">Ne pas remplir <input name="website" tabindex="-1" autocomplete="off"></label>
      <label class="check"><input type="checkbox" name="consent"><span>J'accepte que le club me recontacte au sujet de cette demande. Ces coordonnées ne servent qu'à ça.</span></label>
      ${consentNote(dataHref({ slug }))}
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Envoyer</button>
    </form>`, (sheet) => {
    const f = $<HTMLFormElement>(sheet, '#f-vjoin')!;
    const msg = (t: string) => { $(f, '.form-msg')!.textContent = t; };
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      msg('');
      const d = new FormData(f);
      const v = (k: string) => String(d.get(k) ?? '').trim();
      const done = () => mount(sheet.querySelector('.sheet-body')!, html`
        <div class="pub-thanks"><p class="pub-kicker">Demande envoyée</p><h3>Merci !</h3>
        <p>Le club vous recontacte très vite pour vous accueillir parmi ses bénévoles.</p></div>`);
      if (v('website')) { done(); return; }   // robot : on ne dit rien
      if (!v('name')) { msg('Indiquez votre nom.'); return; }
      if (!v('email') && !v('phone')) { msg('Laissez un e-mail ou un téléphone pour être recontacté.'); return; }
      if (v('email') && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v('email'))) { msg('Adresse e-mail invalide.'); return; }
      if (v('phone') && !/^[0-9 +().-]{6,20}$/.test(v('phone'))) { msg('Numéro de téléphone invalide.'); return; }
      if (d.get('consent') !== 'on') { msg('Cochez la case pour que le club puisse vous recontacter.'); return; }
      const btn = $<HTMLButtonElement>(f, 'button[type=submit]')!;
      btn.disabled = true;
      try {
        await requestVolunteer(slug, { group: v('group') || null, name: v('name'), email: v('email') || null, phone: v('phone') || null, message: v('message') || null });
        done();
      } catch (err) {
        btn.disabled = false;
        msg((err as { code?: string }).code === '54000' ? 'Trop de demandes pour le moment : réessayez demain.' : 'Envoi impossible pour le moment.');
      }
    });
  });
}

export function openTrial(slug: string, clubName: string, categories: string[]) {
  const year = new Date().getFullYear();
  openSheet(`Essayer le basket · ${clubName}`, html`
    <form class="form" id="f-trial" novalidate>
      <div class="row-2">
        <label class="field"><span>Prénom de la personne</span><input name="first_name" maxlength="60" autocomplete="given-name" required></label>
        <label class="field"><span>Année de naissance</span><input name="birth_year" type="number" inputmode="numeric" min="1930" max="${year}" placeholder="${year - 11}"></label>
      </div>
      ${categories.length ? html`<label class="field"><span>Catégorie</span><select name="category"><option value="">Je ne sais pas</option>${categories.map(c => html`<option>${c}</option>`)}</select></label>` : ''}
      <label class="field"><span>Votre nom (parent, si mineur)</span><input name="contact_name" maxlength="80" autocomplete="name"></label>
      <div class="row-2">
        <label class="field"><span>E-mail</span><input name="email" type="email" inputmode="email" maxlength="120" autocomplete="email"></label>
        <label class="field"><span>Téléphone</span><input name="phone" type="tel" inputmode="tel" maxlength="20" autocomplete="tel"></label>
      </div>
      <label class="field"><span>Un mot pour le club (facultatif)</span><textarea name="message" rows="3" maxlength="500"></textarea></label>
      <label class="pub-hp" aria-hidden="true">Ne pas remplir <input name="website" tabindex="-1" autocomplete="off"></label>
      <label class="check"><input type="checkbox" name="consent"><span>J'accepte que le club me recontacte au sujet de cette demande. Ces coordonnées ne servent qu'à ça.</span></label>
      ${consentNote(dataHref({ slug }))}
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Envoyer la demande</button>
    </form>`, (sheet) => {
    const f = $<HTMLFormElement>(sheet, '#f-trial')!;
    const msg = (t: string) => { $(f, '.form-msg')!.textContent = t; };
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      msg('');
      const d = new FormData(f);
      const v = (k: string) => String(d.get(k) ?? '').trim();
      const done = () => mount(sheet.querySelector('.sheet-body')!, html`
        <div class="pub-thanks"><p class="pub-kicker">Demande envoyée</p><h3>Merci !</h3>
        <p>Le club a bien reçu votre demande et vous recontacte pour proposer une séance d'essai.</p></div>`);
      if (v('website')) { done(); return; }   // robot : on ne dit rien
      if (!v('first_name')) { msg('Indiquez le prénom de la personne qui veut essayer.'); return; }
      if (!v('email') && !v('phone')) { msg('Laissez un e-mail ou un téléphone pour être recontacté.'); return; }
      if (v('email') && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v('email'))) { msg('Adresse e-mail invalide.'); return; }
      if (v('phone') && !/^[0-9 +().-]{6,20}$/.test(v('phone'))) { msg('Numéro de téléphone invalide.'); return; }
      const by = v('birth_year') ? Number(v('birth_year')) : null;
      if (by != null && (!Number.isInteger(by) || by < 1930 || by > year)) { msg('Année de naissance invalide.'); return; }
      if (d.get('consent') !== 'on') { msg('Cochez la case pour que le club puisse vous recontacter.'); return; }
      const btn = $<HTMLButtonElement>(f, 'button[type=submit]')!;
      btn.disabled = true;
      try {
        await requestTrial(slug, {
          category: v('category') || null, first_name: v('first_name'), birth_year: by, contact_name: v('contact_name') || null,
          email: v('email') || null, phone: v('phone') || null, message: v('message') || null, consent: true,
        });
        done();
      } catch (err) {
        btn.disabled = false;
        const code = (err as { code?: string }).code;
        msg(code === '54000' ? 'Trop de demandes pour le moment : réessayez demain.'
          : code === 'P0002' ? 'Ce club ne prend pas de demande d’essai en ligne pour le moment.'
          : 'Envoi impossible. Vérifiez votre connexion et réessayez.');
      }
    });
  });
}
